import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import ARNavBar from './ARNavBar';
import ArPastDueSearch from './components/ArPastDueSearch';
import ArPastDueInvoice from './models/arPastDueInvoice';
import { IArPastDueSearchCriteria, useArServiceCtx } from './AccountsReceivableServiceContext';

export const formatDateForDisplay = (value: Date | string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
};

export const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

type SortField = 'client' | 'project' | 'referenceNumber' | 'amount' | 'transactionDate' | 'clientLandmanName';

// Multi-key comparators mirror the Oracle Query buttons' own order_by strings (oid 50/53/56/59/62/65) -
// e.g. the "Client" header sorts CL_NO then PR_NO, "Pr. No." sorts PR_NO then TRAN_DATE, etc.
const SORT_COMPARATORS: Record<SortField, (a: ArPastDueInvoice, b: ArPastDueInvoice) => number> = {
    client: (a, b) => (a.clientNumber - b.clientNumber) || (a.projectNumber - b.projectNumber),
    project: (a, b) => (a.projectNumber - b.projectNumber) || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    referenceNumber: (a, b) => (a.referenceNumber ?? '').localeCompare(b.referenceNumber ?? '') || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    amount: (a, b) => (a.amount - b.amount) || (new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()),
    transactionDate: (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime(),
    clientLandmanName: (a, b) => (a.clientLandmanName ?? '').localeCompare(b.clientLandmanName ?? ''),
};

// Client/project/reference number together identify an invoice for selection purposes - the same
// composite the row's own React key is already built from.
const invoiceKey = (item: ArPastDueInvoice) => `${item.clientNumber}-${item.projectNumber}-${item.referenceNumber}`;

const ArPastDuePage = () => {
    const dispatch = useDispatch();
    const { getArPastDueInvoices } = useArServiceCtx();
    const [invoices, setInvoices] = useState<Array<ArPastDueInvoice>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [sortField, setSortField] = useState<SortField | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(false);
    // Keyed by invoiceKey(), not row/array index - the grid's sort produces a separately-ordered
    // sortedInvoices array from invoices, so an index-based selection (invoices[index].selected)
    // would silently point at a different invoice than the one the checkbox was rendered for as
    // soon as any sort was active. A stable per-invoice key survives re-sorting untouched.
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

    const runQuery = async (criteria: IArPastDueSearchCriteria) => {
        setLoading(true);
        const result = await getArPastDueInvoices(criteria);
        setInvoices(Array.isArray(result) ? result : []);
        setLoading(false);
    };

    const handleSort = (field: SortField) => {
        setSortField(field);
        setSortDescending(field === sortField ? !sortDescending : false);
    };

    const sortedInvoices = React.useMemo(() => {
        if (!sortField) {
            return invoices;
        }
        const sorted = [...invoices].sort(SORT_COMPARATORS[sortField]);
        return sortDescending ? sorted.reverse() : sorted;
    }, [invoices, sortField, sortDescending]);

    const renderSortableHeader = (label: string, field: SortField, style?: React.CSSProperties) => {
        const willSortDescending = field === sortField ? !sortDescending : false;
        return (
            <th
                style={{ whiteSpace: 'nowrap', cursor: 'pointer', ...style }}
                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                onClick={() => handleSort(field)}
            >
                {label}
                <Button
                    style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                    text={''}
                    reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                    iconHoverColor="blue"
                    iconStyle={{ width: '1.25rem', height: '1.25rem', verticalAlign: 'middle' }}
                />
            </th>
        );
    };

    // Mirrors the Select All/Deselect All buttons (oid 109/112) - purely client-side selection
    // state, same as CheckRegisterPage's own setSelection pattern (keyed by invoiceKey() rather
    // than index - see the note on selectedKeys above).
    const setSelection = (selected: boolean) => {
        setSelectedKeys(selected ? new Set(invoices.map(invoiceKey)) : new Set());
    };
    const selectAll = () => setSelection(true);
    const deselectAll = () => setSelection(false);

    const toggleSelected = (key: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // TODO: mirrors oid 115's Go_Block('SEND_EMAIL') flow (generate PDF invoices for the selected
    // rows, then compose/send a past-due dunning email) - not yet built, see the Oracle parity
    // gaps memory for the full trigger shape (GENERATE_INVOICES_TO_FILE / SEND_EMAIL_BUTTON).
    const defineEmailMsg = () => {
        const selected = invoices.filter((item) => selectedKeys.has(invoiceKey(item)));
        console.log('Define Email Msg - selected invoices:', selected);
    };

    // Mirrors oid 118's EXIT_FORM - returns to the main menu.
    const handleExit = () => dispatch(push('/'));

    return (
        <div>
            <ARNavBar />
            <ArPastDueSearch runQuery={runQuery} />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ marginBottom: '.5rem', alignItems: 'center', justifyContent: 'center' }}>
                                <Button className="blue-hover-button" style={{ height: '3rem', marginLeft: '.5rem' }} text="Select All" onClick={selectAll} />
                                <Button className="blue-hover-button" style={{ height: '3rem', marginLeft: '.5rem' }} text="Deselect All" onClick={deselectAll} />
                                <Button className="blue-hover-button" style={{ height: '3rem', marginLeft: '.5rem' }} text="Define Email Msg" onClick={defineEmailMsg} />
                                <Button className="blue-hover-button" style={{ height: '3rem', marginLeft: '.5rem' }} text="Exit" onClick={handleExit} />
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle="Past Due Invoices">
                            <div style={{ overflowY: 'scroll', maxHeight: '32rem' }}>
                                <table style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '1%', padding: 0 }} />
                                            {renderSortableHeader('Client', 'client', { width: '8%' })}
                                            {renderSortableHeader('Pr. No.', 'project', { width: '8%' })}
                                            {renderSortableHeader('Invoice No.', 'referenceNumber', { width: '10%' })}
                                            <th style={{ width: '24%' }}>Description</th>
                                            {renderSortableHeader('Inv. Amount', 'amount', { width: '10%' })}
                                            {renderSortableHeader('Inv. Date', 'transactionDate', { width: '10%' })}
                                            {renderSortableHeader('Client Landman', 'clientLandmanName', { width: '15%' })}
                                            <th style={{ width: '14%' }}>Email</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr>
                                                <td colSpan={9} className="bold-centered">
                                                    <LoadingSpinner />
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && sortedInvoices.map((item) => {
                                            const key = invoiceKey(item);
                                            return (
                                                <tr key={key} style={{ textAlign: 'center' }}>
                                                    <td style={{ padding: 0 }}>
                                                        <RHFCheckbox
                                                            name={`invoice-${key}-selected`}
                                                            checked={selectedKeys.has(key)}
                                                            onChange={() => toggleSelected(key)}
                                                            bodyStyle={{ width: 'auto', margin: 0, display: 'inline-block' }}
                                                        />
                                                    </td>
                                                    <td>{item.clientNumber}</td>
                                                    <td>{item.projectNumber}</td>
                                                    <td>{item.referenceNumber}</td>
                                                    <td style={{ textAlign: 'left' }}>{item.description}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                                                    <td>{formatDateForDisplay(item.transactionDate)}</td>
                                                    <td style={{ textAlign: 'left' }}>{item.clientLandmanName}</td>
                                                    <td style={{ textAlign: 'left' }}>{item.email}</td>
                                                </tr>
                                            );
                                        })}
                                        {!loading && invoices.length === 0 && (
                                            <tr>
                                                <td colSpan={9} className="bold-centered">No Past Due Invoices Found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
        </div>
    );
};

export default ArPastDuePage;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/ArPastDuePage.tsx